import React, { Component } from "react";
import PropTypes from "prop-types";

import LineSeries from "./LineSeries";
import StraightLine from "./StraightLine";

const normalizeStroke = (stroke, fallback) => ({
  color: stroke?.color || fallback.color,
  dash: stroke?.dash || fallback.dash,
  width: stroke?.width ?? fallback.width,
  opacity: stroke?.opacity ?? fallback.opacity,
});

const FALLBACK_PLUS_VI = {
  color: "#00FF00",
  dash: "Solid",
  width: 1,
  opacity: 1,
};
const FALLBACK_MINUS_VI = {
  color: "#FF0000",
  dash: "Solid",
  width: 1,
  opacity: 1,
};

class VortexSeries extends Component {
  constructor(props) {
    super(props);
    this.yAccessorPlusVI = this.yAccessorPlusVI.bind(this);
    this.yAccessorMinusVI = this.yAccessorMinusVI.bind(this);
  }

  static yExtents(datum, index, allData, indicator) {
    const dataArr = Array.isArray(allData)
      ? allData
      : Array.isArray(datum)
        ? datum
        : [datum];

    if (!indicator || !indicator.renderKey) {
      return [0.5, 1.5];
    }

    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;

    dataArr.forEach((item) => {
      const node = item && item[indicator.renderKey];
      if (!node || typeof node !== "object") return;

      const updateExtents = (value) => {
        if (!Number.isFinite(value)) return;
        if (value < min) min = value;
        if (value > max) max = value;
      };

      updateExtents(node.plusVI);
      updateExtents(node.minusVI);
    });

    // Default to 0.5-1.5 if no valid data
    if (
      !Number.isFinite(min) ||
      !Number.isFinite(max) ||
      min === Number.POSITIVE_INFINITY
    ) {
      return [0.5, 1.5];
    }

    // Ensure reference lines are always visible
    // Get appearance config to check threshold values
    const appearance = indicator?.appearance || {};
    const centerLineValue = appearance.centerLine?.value ?? 1.0;
    const overBoughtValue = appearance.overBought?.value ?? 1.1;
    const overSoldValue = appearance.overSold?.value ?? 0.9;

    // Include reference lines in extents
    min = Math.min(min, overSoldValue);
    max = Math.max(max, overBoughtValue);

    // Add padding (5% of range)
    const range = max - min;
    const padding = range * 0.05;

    return [min - padding, max + padding];
  }

  yAccessorPlusVI(d) {
    const { yAccessor } = this.props;
    const vortexData = yAccessor(d);
    return vortexData && vortexData.plusVI;
  }

  yAccessorMinusVI(d) {
    const { yAccessor } = this.props;
    const vortexData = yAccessor(d);
    return vortexData && vortexData.minusVI;
  }

  render() {
    const {
      className,
      strokePlusVI,
      strokeMinusVI,
      centerLine,
      overBought,
      overSold,
    } = this.props;

    const plusStroke = normalizeStroke(strokePlusVI, FALLBACK_PLUS_VI);
    const minusStroke = normalizeStroke(strokeMinusVI, FALLBACK_MINUS_VI);

    // Extract threshold values and strokes
    const extractThresholdValue = (prop, defaultValue, defaultStroke) => {
      if (typeof prop === "number") {
        return { value: prop, stroke: defaultStroke };
      }
      if (prop && typeof prop === "object") {
        if (prop.value !== undefined && prop.stroke) {
          return { value: prop.value, stroke: prop.stroke };
        }
        if (prop.value !== undefined) {
          return { value: prop.value, stroke: defaultStroke };
        }
      }
      return { value: defaultValue, stroke: defaultStroke };
    };

    const centerLineData = extractThresholdValue(centerLine, 1.0, {
      color: "#8795A1",
      dash: "Solid",
      width: 1,
      opacity: 1,
    });
    const overBoughtData = extractThresholdValue(overBought, 1.1, {
      color: "#B8C2CC",
      dash: "Dash",
      width: 1,
      opacity: 1,
    });
    const overSoldData = extractThresholdValue(overSold, 0.9, {
      color: "#B8C2CC",
      dash: "Dash",
      width: 1,
      opacity: 1,
    });

    return (
      <g className={className}>
        {/* Reference lines */}
        <StraightLine
          stroke={centerLineData.stroke}
          yValue={centerLineData.value}
        />
        <StraightLine
          stroke={overBoughtData.stroke}
          yValue={overBoughtData.value}
        />
        <StraightLine
          stroke={overSoldData.stroke}
          yValue={overSoldData.value}
        />

        {/* Vortex lines */}
        <LineSeries
          yAccessor={this.yAccessorPlusVI}
          stroke={plusStroke}
          connectNulls
          highlightOnHover={false}
        />
        <LineSeries
          yAccessor={this.yAccessorMinusVI}
          stroke={minusStroke}
          connectNulls
          highlightOnHover={false}
        />
      </g>
    );
  }
}

VortexSeries.propTypes = {
  className: PropTypes.string,
  yAccessor: PropTypes.func.isRequired,
  strokePlusVI: PropTypes.object,
  strokeMinusVI: PropTypes.object,
  centerLine: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  overBought: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  overSold: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
};

VortexSeries.defaultProps = {
  className: "react-stockcharts-vortex-series",
  strokePlusVI: FALLBACK_PLUS_VI,
  strokeMinusVI: FALLBACK_MINUS_VI,
  centerLine: {
    value: 1.0,
    stroke: { color: "#8795A1", dash: "Solid", width: 1, opacity: 1 },
  },
  overBought: {
    value: 1.1,
    stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
  },
  overSold: {
    value: 0.9,
    stroke: { color: "#B8C2CC", dash: "Dash", width: 1, opacity: 1 },
  },
};

export default VortexSeries;
